import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useAuth } from './hooks/useAuth';
import useInactivityLogout from './hooks/useInactivityLogout';
import MainLayout from './components/layout/MainLayout';
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ChargesPage = lazy(() => import('./pages/ChargesPage'));
const ChargesFileList = lazy(() => import('./pages/ChargesFileList'));
const ChargesFileDetails = lazy(() => import('./pages/ChargesFileDetails'));
const SchedulerPage = lazy(() => import('./pages/SchedulerPage'));
const SchedulerFileList = lazy(() => import('./pages/SchedulerFileList'));
const SchedulerFileDetails = lazy(() => import('./pages/SchedulerFileDetails'));
const DemographicsPage = lazy(() => import('./pages/DemographicsPage'));
const DemographicsFileList = lazy(() => import('./pages/DemographicsFileList'));
const DemographicsFileDetails = lazy(() => import('./pages/DemographicsFileDetails'));
const PfPage = lazy(() => import('./pages/PfPage'));
const ResolutionCenter = lazy(() => import('./components/resolution/ResolutionCenter'));
const CciqActionPage = lazy(() => import('./pages/CciqActionPage'));

// Component to redirect to first available integration
const DefaultRedirect = () => {
  const { integrations, loading } = useAuth();

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div>;
  }

  // Map integration types to their routes
  const integrationRoutes = {
    charges: '/charges',
    scheduler: '/scheduler',
    demographics: '/demographics-dashboard',
    pf: '/pf'
  };

  // Get the first available integration route
  const firstIntegration = integrations && integrations.length > 0 ? integrations[0] : 'charges';
  const defaultRoute = integrationRoutes[firstIntegration] || '/charges';

  return <Navigate to={defaultRoute} replace />;
};

function App() {
  const { isAuthenticated } = useAuth();

  // HIPAA compliance: Auto-logout after 18 minutes of inactivity
  useInactivityLogout(isAuthenticated);

  return (
    <div className="app-container">
      <Suspense fallback={<div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div>}>
        <Routes>
          <Route path="/login" element={!isAuthenticated ? <Login /> : <DefaultRedirect />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/charges" element={<ChargesPage />} />
              <Route path="/files" element={<ChargesFileList />} />
              <Route path="/files/:fileId" element={<ChargesFileDetails />} />
              <Route path="/scheduler" element={<SchedulerPage />} />
              <Route path="/appointments" element={<SchedulerFileList />} />
              <Route path="/appointments/:fileId" element={<SchedulerFileDetails />} />
              <Route path="/demographics-dashboard" element={<DemographicsPage />} />
              <Route path="/demographics" element={<DemographicsFileList />} />
              <Route path="/demographics/:fileId" element={<DemographicsFileDetails />} />
              <Route path="/pf" element={<PfPage />} />
              <Route path="/resolution" element={<ResolutionCenter />} />
              <Route path="/resolution/:practiceId" element={<ResolutionCenter />} />
              <Route path="/cciq/:practiceId" element={<CciqActionPage />} />
              {/* Redirect old /dashboard route to first available integration */}
              <Route path="/dashboard" element={<DefaultRedirect />} />
              {/* Default route - redirect to first available integration */}
              <Route path="/" element={<DefaultRedirect />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
